class HorizontalCarousel extends HTMLElement {
	constructor() {
		super();
		this.scrollContainer = null;
		this.leftButton = null;
		this.rightButton = null;
		this.isScrolling = false;
		this.currentIndex = 0;
		this.resizeObserver = null;
		this.isMobile = false;

		// 新增：触摸状态管理
		this.touchState = {
			startX: 0,
			startY: 0,
			isHorizontal: false,
			isAtStart: false,
			isAtEnd: false,
			lastScrollLeft: 0
		};
	}

	connectedCallback() {
		this.checkMobile();
		this.render();
		this.loadData();
		this.setupEventListeners();
		this.setupResizeObserver();
		this.handleResize();
	}

	disconnectedCallback() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	checkMobile() {
		this.isMobile = window.innerWidth < 1024; // lg断点
	}

	handleResize() {
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.checkMobile();
			if (wasMobile !== this.isMobile) {
				this.render();
				this.loadData();
				this.setupEventListeners();
			}
		});
	}

	get data() {
		const dataAttr = this.getAttribute('data');
		if (dataAttr) {
			try {
				return JSON.parse(decodeURIComponent(dataAttr));
			} catch (e) {
				console.error('Invalid data attribute:', e);
			}
		}
		return [];
	}

	render() {
		if (this.isMobile) {
			this.renderMobile();
		} else {
			this.renderDesktop();
		}
	}

	renderMobile() {
		this.innerHTML = `
			<div class="relative w-full max-w-[120rem] mx-auto">
				<div class="overflow-hidden relative">
					<div class="scroll-container flex transition-transform duration-300 ease-out" style="transform: translateX(0px);">
					</div>
				</div>

				<!-- 移动端分页导航点 -->
				<div class="pagination-dots flex justify-center mt-6 space-x-3" id="pagination-container">
				</div>
			</div>

			<style>
				.pagination-dot {
					cursor: pointer;
					transition: all 0.3s ease;
					transform-origin: center;
				}

				.pagination-dot:hover {
					transform: scale(1.2);
				}

				.pagination-dot.active {
					background: linear-gradient(45deg, #6366f1, #8b5cf6);
					transform: scale(1.3);
					box-shadow: 0 0 12px rgba(139, 92, 246, 0.7);
				}

				/* 移动端卡片样式优化 */
				@media (max-width: 1023px) {
					.car-feature-card {
						width: 96% !important;
						flex-shrink: 0;
						margin: 0 2%;
					}
					
					.scroll-container {
						width: 100%;
						overflow: visible;
					}
				}
			</style>
		`;

		this.scrollContainer = this.querySelector('.scroll-container');
	}

	renderDesktop() {
		this.innerHTML = `
			<div class="relative w-full max-w-[1200px] mx-auto">
				<div class="overflow-hidden relative">
					<div class="scroll-container flex gap-6 transition-transform duration-300 ease-out" style="transform: translateX(0px);">
					</div>
				</div>

				<div class="flex justify-center mt-8 gap-4">
					<button class="nav-button left-button w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors duration-200">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
						</svg>
					</button>
					<button class="nav-button right-button w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors duration-200">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
						</svg>
					</button>
				</div>
			</div>
		`;

		this.scrollContainer = this.querySelector('.scroll-container');
		this.leftButton = this.querySelector('.left-button');
		this.rightButton = this.querySelector('.right-button');
	}

	loadData() {
		const data = this.data;
		this.renderCards(data);

		if (this.isMobile) {
			this.renderPaginationDots(data.length);
			this.adjustCardHeights();
		}

		setTimeout(() => {
			this.updateNavigationState();
		}, 0);
	}

	adjustCardHeights() {
		if (!this.isMobile) return;

		const cards = this.querySelectorAll('.car-feature-card');
		if (!cards.length) return;

		let maxHeight = 0;
		cards.forEach((card) => {
			card.style.height = 'auto';
			const cardHeight = card.offsetHeight;
			maxHeight = Math.max(maxHeight, cardHeight);
		});

		cards.forEach((card) => {
			card.style.height = `${maxHeight}px`;
		});
	}

	renderCards(features) {
		const cardWidth = this.isMobile ? 'w-[96%]' : 'w-[36rem]';

		const cardsHTML = features
			.map(
				(feature, index) => `
				<div class="car-feature-card flex-shrink-0 ${cardWidth}" data-index="${index}">
					<div class="bg-[#131313] rounded-2xl overflow-hidden transition-colors duration-300 h-full ${
						this.isMobile ? 'mx-2' : ''
					}">
						<div class="m-6 rounded-2xl relative h-[20rem] overflow-hidden">
							<img
								src="${feature.image}"
								alt="${feature.title}"
								class="w-full h-full object-cover"
								loading="lazy"
							/>
							<div class="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
						</div>
						<div class="p-6">
							<h3 class="text-2xl lg:text-4xl font-semibold text-white mb-3 leading-tight">
								${feature.title}
							</h3>
							<p class="text-white text-2xl lg:text-2xl leading-relaxed">
								${feature.description}
							</p>
						</div>
					</div>
				</div>
			`
			)
			.join('');

		if (cardsHTML && this.scrollContainer) {
			this.scrollContainer.innerHTML = cardsHTML;
		}
	}

	renderPaginationDots(count) {
		const paginationContainer = this.querySelector('#pagination-container');
		if (!paginationContainer) return;

		const dotsHTML = Array.from({ length: count }, (_, index) => `
			<button class="pagination-dot w-3 h-3 rounded-full transition-all duration-300 ${
				index === 0 ? 'active' : 'bg-gray-600'
			}" data-index="${index}"></button>
		`).join('');

		paginationContainer.innerHTML = dotsHTML;
		this.bindPaginationEvents();
	}

	bindPaginationEvents() {
		const paginationDots = this.querySelectorAll('.pagination-dot');
		paginationDots.forEach((dot) => {
			dot.addEventListener('click', this.handlePaginationClick.bind(this));
		});
	}

	setupEventListeners() {
		if (this.isMobile) {
			this.setupMobileEventListeners();
		} else {
			this.setupDesktopEventListeners();
		}
	}

	setupMobileEventListeners() {
		const container = this.scrollContainer.parentElement;
		if (container) {
			this.addTouchSupport(container);
		}
	}

	setupDesktopEventListeners() {
		this.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
		this.leftButton.addEventListener('click', () => this.scrollLeft());
		this.rightButton.addEventListener('click', () => this.scrollRight());
		this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
		this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
	}

	addTouchSupport(container) {
		let startX = 0;
		let startY = 0;
		let isDown = false;
		let startTransform = 0;

		container.addEventListener('touchstart', (e) => {
			if (this.isScrolling) return;

			isDown = true;
			startX = e.touches[0].clientX;
			startY = e.touches[0].clientY;
			startTransform = this.getCurrentTransform();

			this.scrollContainer.style.transition = 'none';
		}, { passive: true });

		container.addEventListener('touchmove', (e) => {
			if (!isDown) return;

			const currentX = e.touches[0].clientX;
			const currentY = e.touches[0].clientY;
			const diffX = currentX - startX;
			const diffY = currentY - startY;

			if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
				e.preventDefault();

				const newTransform = startTransform + diffX;
				const maxScroll = this.getMaxScroll();
				const clampedTransform = Math.max(Math.min(newTransform, 100), maxScroll - 100);
				this.scrollContainer.style.transform = `translateX(${clampedTransform}px)`;

				this.updatePaginationDotsFromTransform(clampedTransform);
			} else {
				// 垂直滑动，不阻止默认事件，允许页面滚动
			}
		}, { passive: false });

		container.addEventListener('touchend', (e) => {
			if (!isDown) return;
			isDown = false;

			const endX = e.changedTouches[0].clientX;
			const diffX = startX - endX;
			const threshold = 50;
			const velocity = Math.abs(diffX) / 300;

			this.scrollContainer.style.transition = 'transform 0.3s ease-out';

			if (Math.abs(diffX) > threshold || velocity > 0.5) {
				const currentIndex = this.getCurrentCardIndex();
				let targetIndex = diffX > 0 ? Math.min(currentIndex + 1, this.scrollContainer.children.length - 1)
											: Math.max(currentIndex - 1, 0);
				this.scrollToIndex(targetIndex);
				this.updatePaginationDots(targetIndex);
			} else {
				const currentIndex = this.getCurrentCardIndex();
				this.scrollToIndex(currentIndex);
				this.updatePaginationDots(currentIndex);
			}
		}, { passive: true });
	}

	updateBoundaryState() {
		if (!this.scrollContainer) return;

		const currentTransform = this.getCurrentTransform();
		const maxScroll = this.getMaxScroll();

		this.touchState.isAtStart = currentTransform >= 0;
		this.touchState.isAtEnd = currentTransform <= maxScroll;
	}

	getMaxScroll() {
		if (!this.scrollContainer) return 0;

		const containerWidth = this.getContainerWidth();
		const scrollWidth = this.scrollContainer.scrollWidth;

		if (this.isMobile) {
			const cards = this.scrollContainer.children;
			if (cards.length > 0) {
				const cardWidth = containerWidth * 0.96;
				const gap = containerWidth * 0.04;
				const totalWidth = (cardWidth + gap) * cards.length - gap;
				return Math.min(0, containerWidth - totalWidth);
			}
		}

		return Math.min(0, containerWidth - scrollWidth);
	}

	getContainerWidth() {
		const container = this.scrollContainer ? this.scrollContainer.parentElement : this;
		if (!container) return 0;

		const computedStyle = window.getComputedStyle(container);
		const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
		const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

		return container.offsetWidth - paddingLeft - paddingRight;
	}

	updatePaginationDotsFromTransform(transform) {
		if (!this.isMobile) return;

		const containerWidth = this.getContainerWidth();
		const cardWidth = containerWidth * 0.96;
		const gap = containerWidth * 0.04;
		const slideWidth = cardWidth + gap;

		const index = Math.round(-transform / slideWidth);
		const clampedIndex = Math.max(0, Math.min(index, this.scrollContainer.children.length - 1));

		const dots = this.querySelectorAll('.pagination-dot');
		dots.forEach((dot, i) => {
			if (i === clampedIndex) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	handlePaginationClick(e) {
		if (this.isScrolling) return;
		const index = parseInt(e.currentTarget.dataset.index);
		if (!isNaN(index)) {
			this.scrollToIndex(index);
			this.updatePaginationDots(index);
		}
	}

	updatePaginationDots(activeIndex = null) {
		if (!this.isMobile) return;

		const currentIndex = activeIndex !== null ? activeIndex : this.getCurrentCardIndex();
		const dots = this.querySelectorAll('.pagination-dot');

		dots.forEach((dot, index) => {
			if (index === currentIndex) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	setupResizeObserver() {
		this.resizeObserver = new ResizeObserver(() => {
			this.updateNavigationState();
			if (this.isMobile) {
				this.adjustCardHeights();
			}
		});
		this.resizeObserver.observe(this);
	}

	getCardWidth() {
		if (this.scrollContainer && this.scrollContainer.children.length > 0) {
			return this.scrollContainer.children[0].offsetWidth;
		}
		return 0;
	}

	getCardGap() {
		if (this.scrollContainer && this.scrollContainer.children.length > 1) {
			const firstCard = this.scrollContainer.children[0];
			const secondCard = this.scrollContainer.children[1];
			return secondCard.offsetLeft - (firstCard.offsetLeft + firstCard.offsetWidth);
		}
		return 0;
	}

	updateNavigationState() {
		if (!this.scrollContainer || this.scrollContainer.children.length === 0) return;

		if (this.isMobile) {
			const paginationContainer = this.querySelector('#pagination-container');
			if (paginationContainer && this.scrollContainer.children.length > 1) {
				paginationContainer.style.display = 'flex';
			} else if (paginationContainer) {
				paginationContainer.style.display = 'none';
			}
			this.updatePaginationDots();
		} else {
			const containerWidth = this.getContainerWidth();
			const cardCount = this.scrollContainer.children.length;
			const cardWidth = this.getCardWidth();
			const cardGap = this.getCardGap();
			const totalCardsWidth = cardCount * cardWidth + (cardCount - 1) * cardGap;

			const hasEnoughSpace = containerWidth >= totalCardsWidth;

			const navigationContainer = this.querySelector('.flex.justify-center.mt-8');
			if (navigationContainer) {
				navigationContainer.style.display = hasEnoughSpace ? 'none' : 'flex';
			}

			if (hasEnoughSpace) {
				this.centerCards();
			} else {
				this.scrollContainer.style.transform = 'translateX(0px)';
				this.scrollContainer.style.justifyContent = 'flex-start';
			}
		}
	}

	centerCards() {
		this.scrollContainer.style.transform = 'translateX(0px)';
		this.scrollContainer.style.justifyContent = 'center';
	}

	// ✅ 修改后的 handleWheel，允许垂直滚动穿透
	handleWheel(event) {
		const delta = event.deltaY;
		const currentTransform = this.getCurrentTransform();
		const maxScroll = this.getMaxScroll();

		const canScrollLeft = delta < 0 && currentTransform < 0;
		const canScrollRight = delta > 0 && currentTransform > maxScroll;

		if (canScrollLeft || canScrollRight) {
			event.preventDefault();
			const scrollSensitivity = 1;
			let newTransform = currentTransform - delta * scrollSensitivity;
			newTransform = Math.max(Math.min(newTransform, 0), maxScroll);
			this.scrollContainer.style.transform = `translateX(${newTransform}px)`;
			this.scrollContainer.style.justifyContent = 'flex-start';
		} // 否则不阻止，页面可以垂直滚动
	}

	getCurrentCardIndex() {
		if (this.isMobile) {
			const currentTransform = this.getCurrentTransform();
			const containerWidth = this.getContainerWidth();
			const cardWidth = containerWidth * 0.96;
			const gap = containerWidth * 0.04;
			const slideWidth = cardWidth + gap;

			const index = Math.round(-currentTransform / slideWidth);
			return Math.max(0, Math.min(index, this.scrollContainer.children.length - 1));
		} else {
			const currentTransform = this.getCurrentTransform();
			const cardWidth = this.getCardWidth();
			const gap = this.getCardGap();
			const slideWidth = cardWidth + gap;

			const index = Math.round(-currentTransform / slideWidth);
			return Math.max(0, Math.min(index, this.scrollContainer.children.length - 1));
		}
	}

	getCurrentTransform() {
		if (!this.scrollContainer) return 0;
		const style = window.getComputedStyle(this.scrollContainer);
		const matrix = new WebKitCSSMatrix(style.transform);
		return matrix.m41 || 0;
	}

	scrollToIndex(index) {
		if (!this.scrollContainer) return;
		const containerWidth = this.getContainerWidth();

		if (this.isMobile) {
			const cardWidth = containerWidth * 0.96;
			const gap = containerWidth * 0.04;
			const slideWidth = cardWidth + gap;
			const transform = -index * slideWidth;
			this.scrollContainer.style.transition = 'transform 0.3s ease-out';
			this.scrollContainer.style.transform = `translateX(${transform}px)`;
		} else {
			const cardWidth = this.getCardWidth();
			const gap = this.getCardGap();
			const slideWidth = cardWidth + gap;
			const transform = -index * slideWidth;
			this.scrollContainer.style.transition = 'transform 0.3s ease-out';
			this.scrollContainer.style.transform = `translateX(${transform}px)`;
		}
	}

	scrollLeft() {
		const index = this.getCurrentCardIndex();
		const targetIndex = Math.max(index - 1, 0);
		this.scrollToIndex(targetIndex);
	}

	scrollRight() {
		const index = this.getCurrentCardIndex();
		const targetIndex = Math.min(index + 1, this.scrollContainer.children.length - 1);
		this.scrollToIndex(targetIndex);
	}
}

customElements.define('horizontal-carousel', HorizontalCarousel);
